import { useState, useEffect, useRef } from 'react';
import { api, downloadCloneAsset } from '../utils/api';

const CATEGORY_LABELS = {
  pixels: 'Pixels',
  checkouts: 'Checkouts',
  vsl_players: 'Players VSL',
  trackers: 'Trackers',
  forms: 'Forms',
  fonts: 'Fonts',
  external_cdns: 'CDNs externas',
  external_links: 'Links externos',
  external_domains: 'Hosts externos (agregado)',
};

const STATUS_STYLES = {
  pending:  { bg: 'rgba(245,158,11,0.10)', color: 'var(--accent-a)', label: 'Pendente' },
  running:  { bg: 'rgba(67,97,238,0.10)',  color: 'var(--accent)',   label: 'Rodando' },
  done:     { bg: 'rgba(6,185,111,0.10)',  color: 'var(--accent-g)', label: 'Pronto' },
  error:    { bg: 'rgba(239,68,68,0.10)',  color: 'var(--accent-r)', label: 'Erro' },
};

const fmtBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min atras`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atras`;
  return `${Math.floor(diff / 86400)}d atras`;
};

export default function CloneSite() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const pollRef = useRef(null);

  const load = () => {
    api.cloneSiteList()
      .then(d => setJobs(Array.isArray(d) ? d : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Auto-refresh enquanto houver jobs em pending/running
  useEffect(() => {
    const hasActive = jobs.some(j => j.status === 'pending' || j.status === 'running');
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(load, 3000);
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [jobs]);

  const submit = (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!u) return;
    if (!u.startsWith('http://') && !u.startsWith('https://')) {
      setError('URL precisa comecar com http:// ou https://');
      return;
    }
    setSubmitting(true);
    setError(null);
    api.cloneSiteCreate(u)
      .then(() => { setUrl(''); load(); })
      .catch(e => setError(e.message))
      .finally(() => setSubmitting(false));
  };

  const toggleExpand = (jobId) => {
    if (expanded === jobId) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    setExpanded(jobId);
    setDetail(null);
    setDetailLoading(true);
    api.cloneSiteGet(jobId)
      .then(d => setDetail(d))
      .catch(e => setError(e.message))
      .finally(() => setDetailLoading(false));
  };

  return (
    <div style={s.page}>
      <div style={s.topBar}>
        <div>
          <div style={s.title}>Clone Site</div>
          <div style={s.subtitle}>Baixe LP de concorrente com pontos a trocar marcados (pixels, checkouts, VSL, trackers).</div>
        </div>
      </div>

      <form style={s.form} onSubmit={submit}>
        <input
          type="url"
          placeholder="https://www.exemplo.com/lp-concorrente"
          value={url}
          onChange={e => setUrl(e.target.value)}
          style={s.input}
          required
        />
        <button type="submit" disabled={submitting || !url.trim()} style={{ ...s.btn, opacity: (submitting || !url.trim()) ? 0.5 : 1 }}>
          {submitting ? 'Criando...' : 'Clonar LP'}
        </button>
      </form>
      {error && <div style={s.error}>{error}</div>}

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>URL</th>
              <th style={{ ...s.th, width: 100 }}>Status</th>
              <th style={{ ...s.th, width: 220 }}>Stats</th>
              <th style={{ ...s.th, width: 120 }}>Criado</th>
              <th style={{ ...s.th, width: 220, textAlign: 'right' }}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', color: 'var(--text3)', padding: 40 }}>Carregando...</td></tr>
            )}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', color: 'var(--text3)', padding: 40 }}>Nenhum clone ainda. Cole uma URL acima.</td></tr>
            )}
            {jobs.map(j => {
              const st = STATUS_STYLES[j.status] || STATUS_STYLES.pending;
              const isExpanded = expanded === j.id;
              const stats = j.stats || {};
              return (
                <Row key={j.id}
                  job={j}
                  status={st}
                  stats={stats}
                  isExpanded={isExpanded}
                  detail={isExpanded ? detail : null}
                  detailLoading={isExpanded && detailLoading}
                  onToggle={() => toggleExpand(j.id)}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={s.footer}>{jobs.length} clone(s) total · auto-refresh quando ha job rodando</div>
    </div>
  );
}

function Row({ job, status, stats, isExpanded, detail, detailLoading, onToggle }) {
  const handleDownload = (e) => {
    e.stopPropagation();
    downloadCloneAsset(job.id, 'download').catch(err => alert('Erro ao baixar: ' + err.message));
  };
  const handleInstructions = (e) => {
    e.stopPropagation();
    downloadCloneAsset(job.id, 'instructions').catch(err => alert('Erro ao baixar: ' + err.message));
  };

  return (
    <>
      <tr style={s.tr} onClick={onToggle}>
        <td style={s.td}>
          <div style={s.url} title={job.source_url}>{job.source_url}</div>
          {job.offer_id && <div style={s.urlSub}>offer: {job.offer_id.slice(0, 8)}</div>}
        </td>
        <td style={s.td}>
          <span style={{ ...s.statusBadge, background: status.bg, color: status.color }}>{status.label}</span>
          {job.status === 'error' && job.error_msg && (
            <div style={s.errMsg} title={job.error_msg}>{job.error_msg.slice(0, 60)}</div>
          )}
        </td>
        <td style={s.td}>
          {job.status === 'done' ? (
            <div style={s.statsLine}>
              <span title="Findings totais"><b>{stats.total_findings || 0}</b> pts</span>
              <span style={s.sep}>·</span>
              <span title="Assets baixados"><b>{stats.assets_downloaded || 0}</b> assets</span>
              <span style={s.sep}>·</span>
              <span title="Tamanho">{fmtBytes(stats.total_bytes || 0)}</span>
            </div>
          ) : (
            <span style={s.dim}>—</span>
          )}
        </td>
        <td style={{ ...s.td, color: 'var(--text3)', fontSize: 11 }}>{fmtAgo(job.created_at)}</td>
        <td style={{ ...s.td, textAlign: 'right' }}>
          {job.has_zip && (
            <button onClick={handleDownload} style={s.actBtn}>ZIP</button>
          )}
          {job.has_zip && (
            <button onClick={handleInstructions} style={{ ...s.actBtn, marginLeft: 6 }}>MD</button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onToggle(); }} style={{ ...s.actBtnGhost, marginLeft: 6 }}>
            {isExpanded ? '−' : 'Detalhes'}
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={5} style={{ padding: 0, background: 'var(--surface2)' }}>
            <DetailPanel detail={detail} loading={detailLoading} stats={stats} />
          </td>
        </tr>
      )}
    </>
  );
}

function DetailPanel({ detail, loading, stats }) {
  if (loading) return <div style={s.detail}>Carregando detalhes...</div>;
  if (!detail) return <div style={s.detail}>Sem detalhes.</div>;

  const findings = (detail.meta && detail.meta.findings) || {};
  const byCat = stats.by_category || {};
  const categories = Object.keys(CATEGORY_LABELS).filter(c => (byCat[c] || 0) > 0);

  return (
    <div style={s.detail}>
      <div style={s.detailMetrics}>
        {categories.map(cat => (
          <div key={cat} style={s.detailCard}>
            <div style={s.detailLabel}>{CATEGORY_LABELS[cat]}</div>
            <div style={s.detailValue}>{byCat[cat]}</div>
          </div>
        ))}
      </div>
      {categories.map(cat => {
        const items = findings[cat] || [];
        if (items.length === 0) return null;
        return (
          <div key={cat} style={s.detailSection}>
            <div style={s.detailSectionTitle}>{CATEGORY_LABELS[cat]} ({items.length})</div>
            <div style={s.findingList}>
              {items.slice(0, 20).map((f, i) => (
                <div key={i} style={s.finding}>
                  <span style={s.findingType}>{f.type}</span>
                  <code style={s.findingValue} title={f.value}>{(f.value || '').slice(0, 100)}</code>
                  <span style={s.findingFile} title={`${f.file}:${f.line}`}>{f.file}:{f.line}</span>
                </div>
              ))}
              {items.length > 20 && <div style={s.dim}>... +{items.length - 20} (veja CLONE_INSTRUCTIONS.md no ZIP)</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const s = {
  page: { padding: '24px', maxWidth: 1400, margin: '0 auto' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  title: { fontSize: 13, fontWeight: 700 },
  subtitle: { fontSize: 12, color: 'var(--text3)', marginTop: 4 },

  form: { display: 'flex', gap: 8, marginBottom: 12 },
  input: {
    flex: 1, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6,
    background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', outline: 'none',
  },
  btn: {
    padding: '8px 18px', background: 'var(--accent)', color: '#fff', border: 'none',
    borderRadius: 6, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  },
  error: {
    padding: '8px 12px', background: 'rgba(239,68,68,0.10)', color: 'var(--accent-r)',
    borderRadius: 6, fontSize: 12, marginBottom: 12,
  },

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 14px', fontSize: 10, color: 'var(--text3)',
    textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)',
    fontWeight: 600, whiteSpace: 'nowrap',
  },
  td: { padding: '10px 14px', borderBottom: '1px solid var(--surface3)', verticalAlign: 'middle' },
  tr: { cursor: 'pointer' },

  url: { color: 'var(--accent)', fontSize: 12, fontWeight: 500, maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 },
  urlSub: { fontSize: 10, color: 'var(--text3)', marginTop: 2 },

  statusBadge: { padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  errMsg: { fontSize: 10, color: 'var(--accent-r)', marginTop: 4, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  statsLine: { fontSize: 12, color: 'var(--text2)' },
  sep: { color: 'var(--text3)', margin: '0 6px' },
  dim: { color: 'var(--text3)', fontSize: 11 },

  actBtn: {
    padding: '4px 12px', background: 'var(--accent)', color: '#fff', border: 'none',
    borderRadius: 4, fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  },
  actBtnGhost: {
    padding: '4px 12px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)',
    borderRadius: 4, fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  },

  detail: { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 },
  detailMetrics: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  detailCard: { flex: '0 1 140px', padding: '10px 14px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' },
  detailLabel: { fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 },
  detailValue: { fontSize: 18, fontWeight: 700, marginTop: 2, color: 'var(--text)' },
  detailSection: {},
  detailSectionTitle: { fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 8 },

  findingList: { display: 'flex', flexDirection: 'column', gap: 4, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', padding: '8px 12px' },
  finding: { display: 'grid', gridTemplateColumns: '110px 1fr 200px', gap: 12, alignItems: 'center', fontSize: 11 },
  findingType: { color: 'var(--accent)', fontWeight: 600 },
  findingValue: { color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 10 },
  findingFile: { color: 'var(--text3)', fontSize: 10, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  footer: { marginTop: 8, fontSize: 11, color: 'var(--text3)', textAlign: 'right' },
};
